import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Drawer, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtTime, toastErr } from './common';
import { StreamRef, askReason, runOp } from './opsCommon';
import StreamOpsDrawer from './StreamOpsDrawer';

const { Text } = Typography;

const Check = ({ on, label }) => <Tag color={on ? 'green' : 'default'} style={{ marginInlineEnd: 2 }}>{label}</Tag>;

const RecordsDrawer = ({ id, onClose }) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        setD(null);
        if (id) tmAdminExtService.getDomain(id).then((r) => setD(r?.data || null)).catch((e) => toastErr(e, '加载失败'));
    }, [id]);
    return (
        <Drawer open={!!id} onClose={onClose} width={720} title={d?.domain || ''} destroyOnClose>
            {d && (
                <Table size="small" rowKey="key" pagination={false} dataSource={d.dns_records || []} columns={[
                    { title: '类型', dataIndex: 'type', width: 70 },
                    { title: '主机', dataIndex: 'fqdn', render: (v) => <Text copyable style={{ fontSize: 12 }}>{v}</Text> },
                    { title: '值', dataIndex: 'value', render: (v) => <Text copyable={{ text: v }} style={{ fontSize: 12, wordBreak: 'break-all' }}>{v.length > 80 ? `${v.slice(0, 80)}…` : v}</Text> },
                    { title: '', dataIndex: 'key', width: 80, render: (k) => <Check on={d.checks?.[k]} label={k} /> },
                ]} />
            )}
        </Drawer>
    );
};

const Domains = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [disableOk, setDisableOk] = useState(true);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({});
    const [busy, setBusy] = useState(null);
    const [streamId, setStreamId] = useState(null);
    const [recordsId, setRecordsId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminExtService.listDomains({ ...f, page: page.current, per_page: page.pageSize });
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
            setDisableOk(r?.data?.disable_supported !== false);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [f, page]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };
    const op = async (id, fn, text) => { setBusy(id); await runOp(fn, text, load); setBusy(null); };

    const columns = [
        { title: '域名', dataIndex: 'domain', render: (v, d) => <Typography.Link onClick={() => setRecordsId(d.id)}>{v}</Typography.Link> },
        { title: '所有者', key: 'u', render: (_, d) => <span>#{d.user.id} {d.user.username}<br /><Text type="secondary" style={{ fontSize: 12 }}>{d.user.email}</Text></span> },
        { title: '绑定', key: 's', width: 170, render: (_, d) => (d.stream ? <StreamRef stream={d.stream} onOpen={setStreamId} /> : <Text type="secondary">账号共用</Text>) },
        { title: 'DKIM', dataIndex: 'dkim_selector', width: 70 },
        {
            title: '状态', key: 'st', width: 130,
            render: (_, d) => (
                <Space size={4} wrap>
                    {d.verified ? <Tooltip title={fmtTime(d.verified_at)}><Tag color="green">已验证</Tag></Tooltip> : <Tag>未验证</Tag>}
                    {d.disabled && <Tooltip title={`${d.disabled_reason || ''} ${fmtTime(d.disabled_at)}`}><Tag color="red">已停用</Tag></Tooltip>}
                </Space>
            ),
        },
        {
            title: '检查', key: 'c', width: 230,
            render: (_, d) => <span><Check on={d.checks.dkim} label="DKIM" /><Check on={d.checks.spf} label="SPF" /><Check on={d.checks.return_path} label="退信" /><Check on={d.checks.dmarc} label="DMARC" /></span>,
        },
        { title: '检测于', dataIndex: 'last_check_at', width: 150, render: fmtTime },
        {
            title: '', key: 'op', width: 200,
            render: (_, d) => (
                <Space size={0} wrap>
                    <Button type="link" size="small" loading={busy === d.id} onClick={() => op(d.id, () => tmAdminExtService.verifyDomain(d.id), '已重新检测')}>检测</Button>
                    {disableOk && (d.disabled
                        ? <Button type="link" size="small" onClick={() => op(d.id, () => tmAdminExtService.enableDomain(d.id), '已启用')}>启用</Button>
                        : <Button type="link" size="small" danger onClick={() => askReason(`停用 ${d.domain}`, '管理员停用', (r) => op(d.id, () => tmAdminExtService.disableDomain(d.id, r), '已停用'))}>停用</Button>)}
                    <Popconfirm title={`删除 ${d.domain}？DKIM 密钥一并删除`} okButtonProps={{ danger: true }} onConfirm={() => op(d.id, () => tmAdminExtService.deleteDomain(d.id), '已删除')}>
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card title="发信域名" extra={<Button icon={<ReloadOutlined />} onClick={load} />}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder="域名" style={{ width: 220 }} onSearch={(v) => setFilter({ q: v.trim() || undefined })} />
                <Input.Search allowClear placeholder="用户 ID / 邮箱" style={{ width: 200 }} onSearch={(v) => setFilter({ user: v.trim() || undefined })} />
                <InputNumber placeholder="发信流 ID" min={1} style={{ width: 110 }} onChange={(v) => setFilter({ stream_id: v || undefined })} />
                <Select allowClear placeholder="验证" style={{ width: 100 }} options={[{ value: 1, label: '已验证' }, { value: 0, label: '未验证' }]} onChange={(v) => setFilter({ verified: v })} />
                {disableOk && <Select allowClear placeholder="停用" style={{ width: 100 }} options={[{ value: 1, label: '已停用' }, { value: 0, label: '正常' }]} onChange={(v) => setFilter({ disabled: v })} />}
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 1200 }}
                pagination={{ ...page, total, showSizeChanger: true, onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <StreamOpsDrawer streamId={streamId} onClose={() => setStreamId(null)} onChanged={load} />
            <RecordsDrawer id={recordsId} onClose={() => setRecordsId(null)} />
        </Card>
    );
};

export default Domains;
